import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { iamApi } from '@/api/iam.api';
import { useAuthStore } from '@/store/auth.store';
import type { User } from '@/types';
import type { IamUserListItem, PersonalAccessToken } from '@/types/iam.types';
import { accountProfileToUser } from '@/lib/mapIamProfile';

const userTypeLabels: Record<string, string> = {
  account: '主账号',
  user: '子账号',
};

function UserInfoCard({ user, isLoading }: { user: User | null; isLoading: boolean }) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>我的信息</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-40" />
        </CardContent>
      </Card>
    );
  }

  if (!user) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          无法获取用户信息
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>我的信息</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500/20 to-indigo-600/10 flex items-center justify-center">
            <span className="text-2xl font-bold text-indigo-600">
              {(user.name || 'U')[0]?.toUpperCase() || 'U'}
            </span>
          </div>
          <div>
            <h3 className="text-xl font-semibold">{user.name || 'Unknown'}</h3>
            <p className="text-sm text-muted-foreground">
              {userTypeLabels[user.user_type] || user.user_type || 'Unknown'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">用户 ID</span>
            <p className="font-medium">{user.uid || '-'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">用户类型</span>
            <p className="font-medium">{userTypeLabels[user.user_type] || user.user_type || '-'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">主账号 ID</span>
            <p className="font-medium font-mono text-xs">{user.account_id || '-'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">IAM 用户 ID</span>
            <p className="font-medium font-mono text-xs">{user.iam_user_id || '-'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">域 ID</span>
            <p className="font-medium font-mono text-xs">{user.domain_id || '-'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">域别名</span>
            <p className="font-medium">{user.domain_alias || '-'}</p>
          </div>
        </div>

        {user.preferences && user.preferences !== '{}' && (
          <div className="mt-4">
            <span className="text-muted-foreground text-sm">偏好设置</span>
            <pre className="mt-1 p-3 rounded-lg bg-muted/50 text-xs overflow-auto max-h-32">
              {user.preferences}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SubAccountList() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [formName, setFormName] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formPasswordConfirm, setFormPasswordConfirm] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formComment, setFormComment] = useState('');
  const [formOpenapi, setFormOpenapi] = useState(true);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<IamUserListItem | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['iam-users', 'subaccounts'],
    queryFn: () => iamApi.listIAMUsers({ page: 1, limit: 50 }),
  });

  const createMutation = useMutation({
    mutationFn: (d: {
      name: string;
      password: string;
      display_name?: string;
      email?: string;
      phone?: string;
      comment?: string;
      enable_openapi?: boolean;
    }) => iamApi.createIAMUser(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['iam-users'] });
      closeCreate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => iamApi.deleteIAMUser({ user_id: userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['iam-users'] });
      setDeleteTarget(null);
    },
  });

  function openCreate() {
    setFormName('');
    setFormPassword('');
    setFormPasswordConfirm('');
    setFormDisplayName('');
    setFormEmail('');
    setFormPhone('');
    setFormComment('');
    setFormOpenapi(true);
    setFormError('');
    setShowCreate(true);
  }

  function closeCreate() {
    setShowCreate(false);
    setFormError('');
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!formName.trim()) {
      setFormError('请输入用户名');
      return;
    }
    if (!formPassword || formPassword.length < 8) {
      setFormError('密码至少 8 位');
      return;
    }
    if (formPassword !== formPasswordConfirm) {
      setFormError('两次输入的密码不一致');
      return;
    }
    createMutation.mutate({
      name: formName.trim(),
      password: formPassword,
      display_name: formDisplayName.trim() || undefined,
      email: formEmail.trim() || undefined,
      phone: formPhone.trim() || undefined,
      comment: formComment.trim() || undefined,
      enable_openapi: formOpenapi,
    });
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>子账号列表</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>子账号列表</CardTitle>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            重试
          </Button>
        </CardHeader>
        <CardContent className="py-8 text-center text-destructive">加载失败，请重试</CardContent>
      </Card>
    );
  }

  const users = data?.items ?? [];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>子账号列表</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">共 {users.length} 个子账号</p>
          </div>
          <Button size="sm" onClick={openCreate}>
            创建子账号
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* User list */}
          {users.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">暂无子账号</div>
          ) : (
            <div className="space-y-2">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-600/10 flex items-center justify-center shrink-0">
                      <span className="text-blue-600 font-medium">
                        {(user.name || 'U')[0]?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-xs text-muted-foreground">
                        ID: {user.id}
                        {user.email && <span className="ml-3">{user.email}</span>}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setDeleteTarget(user)}
                  >
                    删除
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={closeCreate}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-5 pb-4 border-b">
              <h3 className="text-base font-semibold">创建子账号</h3>
              <p className="text-xs text-muted-foreground mt-1">
                为当前租户创建一个新的 IAM 子账号
              </p>
            </div>

            <form onSubmit={handleCreate} className="flex flex-col overflow-hidden">
              <div className="px-6 py-4 space-y-4 overflow-y-auto">
                {/* Account info */}
                <div className="space-y-3">
                  <p className="text-xs font-medium text-muted-foreground">账号信息</p>
                  <div className="space-y-1.5">
                    <Label className="text-sm">
                      <span className="text-red-400 mr-0.5">*</span>用户名
                    </Label>
                    <Input
                      value={formName}
                      onChange={(e) => {
                        setFormName(e.target.value);
                        setFormError('');
                      }}
                      placeholder="用于登录的唯一标识"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">
                      <span className="text-red-400 mr-0.5">*</span>密码
                    </Label>
                    <Input
                      type="password"
                      value={formPassword}
                      onChange={(e) => {
                        setFormPassword(e.target.value);
                        setFormError('');
                      }}
                      placeholder="至少 8 位"
                    />
                  </div>
                  {formPassword &&
                    (() => {
                      const rules = [
                        { pass: formPassword.length >= 8, label: '8位以上' },
                        { pass: /[a-z]/.test(formPassword), label: '小写字母' },
                        { pass: /[A-Z]/.test(formPassword), label: '大写字母' },
                        { pass: /[0-9]/.test(formPassword), label: '数字' },
                        { pass: /[^a-zA-Z0-9]/.test(formPassword), label: '特殊字符' },
                      ];
                      const passed = rules.filter((r) => r.pass).length;
                      const color =
                        passed <= 2 ? 'bg-red-400' : passed <= 3 ? 'bg-amber-400' : 'bg-green-500';
                      const label =
                        passed <= 2 ? '弱' : passed <= 3 ? '中' : passed <= 4 ? '强' : '很强';
                      const labelColor =
                        passed <= 2
                          ? 'text-red-500'
                          : passed <= 3
                            ? 'text-amber-500'
                            : 'text-green-600';
                      return (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1 flex-1">
                              {rules.map((_, i) => (
                                <div
                                  key={i}
                                  className={`h-1.5 flex-1 rounded-full transition-colors ${i < passed ? color : 'bg-muted'}`}
                                />
                              ))}
                            </div>
                            <span className={`text-xs font-medium ${labelColor}`}>{label}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground">
                            需包含：
                            {rules.map((r, i) => (
                              <span key={r.label}>
                                {i > 0 && '、'}
                                <span className={r.pass ? 'text-green-600' : ''}>{r.label}</span>
                              </span>
                            ))}
                          </p>
                        </div>
                      );
                    })()}
                  <div className="space-y-1.5">
                    <Label className="text-sm">
                      <span className="text-red-400 mr-0.5">*</span>确认密码
                    </Label>
                    <Input
                      type="password"
                      value={formPasswordConfirm}
                      onChange={(e) => {
                        setFormPasswordConfirm(e.target.value);
                        setFormError('');
                      }}
                      placeholder="再次输入密码"
                    />
                  </div>
                </div>

                <hr />

                {/* Profile & settings */}
                <div className="space-y-3">
                  <p className="text-xs font-medium text-muted-foreground">补充信息（选填）</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-sm">显示名称</Label>
                      <Input
                        value={formDisplayName}
                        onChange={(e) => setFormDisplayName(e.target.value)}
                        placeholder="昵称"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm">邮箱</Label>
                      <Input
                        type="email"
                        value={formEmail}
                        onChange={(e) => setFormEmail(e.target.value)}
                        placeholder="email"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm">手机号</Label>
                      <Input
                        value={formPhone}
                        onChange={(e) => setFormPhone(e.target.value)}
                        placeholder="手机号"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 space-y-1.5">
                      <Label className="text-sm">备注</Label>
                      <Input
                        value={formComment}
                        onChange={(e) => setFormComment(e.target.value)}
                        placeholder="关于该账号的补充说明"
                      />
                    </div>
                    <div className="pt-5">
                      <label className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={formOpenapi}
                          onChange={(e) => setFormOpenapi(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm">OpenAPI 访问</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Errors */}
                {formError && <p className="text-sm text-red-500">{formError}</p>}
                {createMutation.error && (
                  <div className="rounded-md bg-red-50 border border-red-200 p-2.5 text-sm text-red-600">
                    {(() => {
                      const err = createMutation.error as {
                        response?: { data?: { msg?: string; message?: string } };
                        message?: string;
                      };
                      return (
                        err.response?.data?.msg ||
                        err.response?.data?.message ||
                        err.message ||
                        '创建失败'
                      );
                    })()}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 px-6 py-3 border-t bg-muted/30">
                <Button type="button" variant="outline" size="sm" onClick={closeCreate}>
                  取消
                </Button>
                <Button type="submit" size="sm" disabled={createMutation.isPending}>
                  {createMutation.isPending ? '创建中...' : '创建'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4 space-y-4">
            <h3 className="text-lg font-semibold">确认删除</h3>
            <p className="text-sm text-muted-foreground">
              确定要删除子账号{' '}
              <span className="font-medium text-foreground">{deleteTarget.name}</span> 吗？
              该用户的所有权限和访问凭证将被清除。
            </p>
            <p className="text-xs text-red-600 font-medium">此操作不可撤销！</p>
            {deleteMutation.error && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-600">
                {(() => {
                  const err = deleteMutation.error as {
                    response?: { data?: { msg?: string; message?: string } };
                    message?: string;
                  };
                  return (
                    err.response?.data?.msg ||
                    err.response?.data?.message ||
                    err.message ||
                    '删除失败'
                  );
                })()}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
              >
                取消
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? '删除中...' : '确认删除'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TokenItem({
  pat,
  onToggle,
  onDelete,
  togglePending,
  deletePending,
}: {
  pat: PersonalAccessToken;
  onToggle: (id: string, is_active: boolean) => void;
  onDelete: (id: string, name: string) => void;
  togglePending: boolean;
  deletePending: boolean;
}) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">{pat.name || pat.id}</p>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
            <span>创建于 {pat.created_at || '-'}</span>
            {pat.expires_at && <span>过期时间 {pat.expires_at}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
            pat.is_active
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-slate-50 text-slate-500 border-slate-200'
          }`}
        >
          {pat.is_active ? '启用' : '禁用'}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => onToggle(pat.id, !pat.is_active)}
          disabled={togglePending}
        >
          {pat.is_active ? '禁用' : '启用'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={() => onDelete(pat.id, pat.name || pat.id)}
          disabled={deletePending}
        >
          删除
        </Button>
      </div>
    </div>
  );
}

function CollapsibleTokenGroup({
  userName,
  tokens,
  onToggle,
  onDelete,
  togglePending,
  deletePending,
}: {
  userName: string;
  tokens: PersonalAccessToken[];
  onToggle: (id: string, is_active: boolean) => void;
  onDelete: (id: string, name: string) => void;
  togglePending: boolean;
  deletePending: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card>
      <button
        type="button"
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <span className="text-sm font-medium text-blue-600">
              {userName[0]?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="text-left">
            <p className="text-sm font-medium">{userName}</p>
            <p className="text-xs text-muted-foreground">{tokens.length} 个 Token</p>
          </div>
        </div>
        <svg
          className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <CardContent className="pt-0">
          <div className="space-y-2">
            {tokens.map((pat) => (
              <TokenItem
                key={pat.id}
                pat={pat}
                onToggle={onToggle}
                onDelete={onDelete}
                togglePending={togglePending}
                deletePending={deletePending}
              />
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function PersonalAccessTokenList() {
  const queryClient = useQueryClient();
  const { user: authUser } = useAuthStore();
  const isAccount = authUser?.user_type === 'account';

  const [showCreate, setShowCreate] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [validDays, setValidDays] = useState(30);
  const [formError, setFormError] = useState('');
  const [generatedToken, setGeneratedToken] = useState('');
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['personal-access-tokens'],
    queryFn: () => iamApi.listPersonalAccessTokens(),
  });

  const { data: subAccounts } = useQuery({
    queryKey: ['iam-users', 'subaccounts-for-tokens'],
    queryFn: () => iamApi.listIAMUsers({ page: 1, limit: 100 }),
    enabled: isAccount,
  });

  const createMutation = useMutation({
    mutationFn: (d: { name: string; expires_in?: number }) => iamApi.createPersonalAccessToken(d),
    onSuccess: (res) => {
      setGeneratedToken(res.token);
      queryClient.invalidateQueries({ queryKey: ['personal-access-tokens'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => iamApi.deletePersonalAccessToken({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personal-access-tokens'] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (d: { id: string; is_active: boolean }) =>
      iamApi.setPersonalAccessTokenActiveStatus(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personal-access-tokens'] });
    },
  });

  function openCreate() {
    setShowCreate(true);
    setTokenName('');
    setValidDays(30);
    setFormError('');
    setGeneratedToken('');
    setCopied(false);
  }

  function handleCreate() {
    if (!tokenName.trim()) {
      setFormError('请输入 Token 名称');
      return;
    }
    const expiresIn = validDays > 0 ? validDays * 86400 : undefined;
    createMutation.mutate({ name: tokenName.trim(), expires_in: expiresIn });
  }

  async function handleCopy() {
    await navigator.clipboard.writeText(generatedToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleToggle(id: string, is_active: boolean) {
    toggleMutation.mutate({ id, is_active });
  }

  function handleDelete(id: string, name: string) {
    if (window.confirm(`确定删除 Token "${name}" 吗？`)) {
      deleteMutation.mutate(id);
    }
  }

  const tokens = data?.items ?? [];

  // Build user name map from sub-accounts
  const userNameMap = new Map<string, string>();
  if (subAccounts?.items) {
    for (const u of subAccounts.items) {
      userNameMap.set(u.id, u.name || u.id);
    }
  }

  // Determine current user's ID
  const currentUserId = authUser?.user_id;

  // Split tokens: my tokens (no uid or matches current) vs sub-user tokens
  const myTokens: PersonalAccessToken[] = [];
  const groupedByUser = new Map<string, { userName: string; tokens: PersonalAccessToken[] }>();

  for (const t of tokens) {
    if (!isAccount || !t.uid || t.uid === currentUserId) {
      myTokens.push(t);
    } else {
      const uid = t.uid;
      if (!groupedByUser.has(uid)) {
        groupedByUser.set(uid, {
          userName: userNameMap.get(uid) || uid,
          tokens: [],
        });
      }
      groupedByUser.get(uid)!.tokens.push(t);
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Personal Access Tokens</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">加载失败，请重试</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* My tokens card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{isAccount ? '主账号 Token' : 'Personal Access Tokens'}</CardTitle>
          <Button size="sm" onClick={openCreate}>
            创建 Token
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Create form */}
          {showCreate && (
            <div className="rounded-lg border p-4 space-y-4 bg-muted/30">
              {generatedToken ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-green-700">Token 创建成功</p>
                  <div className="relative bg-slate-900 rounded-lg p-3">
                    <pre className="text-xs font-mono text-green-400 break-all whitespace-pre-wrap pr-16 leading-relaxed">
                      {generatedToken}
                    </pre>
                    <Button
                      variant="outline"
                      size="sm"
                      className="absolute top-2 right-2 h-7 text-xs bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white"
                      onClick={handleCopy}
                    >
                      {copied ? '已复制' : '复制'}
                    </Button>
                  </div>
                  <p className="text-xs text-amber-600">此 Token 仅显示一次，请务必复制保存</p>
                  <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>
                    关闭
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">
                        <span className="text-red-400 mr-0.5">*</span>
                        名称
                      </Label>
                      <Input
                        value={tokenName}
                        onChange={(e) => {
                          setTokenName(e.target.value);
                          setFormError('');
                        }}
                        placeholder="如：CI/CD Token"
                        className={formError ? 'border-red-300' : ''}
                      />
                      {formError && <p className="text-xs text-red-500">{formError}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">有效期（天）</Label>
                      <Input
                        type="number"
                        min={1}
                        max={3650}
                        value={validDays}
                        onChange={(e) => setValidDays(Number(e.target.value))}
                      />
                    </div>
                  </div>
                  {createMutation.error && (
                    <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-600">
                      {(() => {
                        const err = createMutation.error as {
                          response?: { data?: { msg?: string; message?: string } };
                          message?: string;
                        };
                        return (
                          err.response?.data?.msg ||
                          err.response?.data?.message ||
                          err.message ||
                          '创建失败，请重试'
                        );
                      })()}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleCreate} disabled={createMutation.isPending}>
                      {createMutation.isPending ? '创建中...' : '创建'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>
                      取消
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* My token list */}
          {myTokens.length === 0 && !showCreate ? (
            <div className="py-6 text-center text-muted-foreground">
              暂无 Token，点击上方按钮创建
            </div>
          ) : (
            <div className="space-y-2">
              {myTokens.map((pat) => (
                <TokenItem
                  key={pat.id}
                  pat={pat}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                  togglePending={toggleMutation.isPending}
                  deletePending={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sub-user tokens grouped by user - only for account type */}
      {isAccount &&
        groupedByUser.size > 0 &&
        Array.from(groupedByUser.entries()).map(([uid, group]) => (
          <CollapsibleTokenGroup
            key={uid}
            userName={group.userName}
            tokens={group.tokens}
            onToggle={handleToggle}
            onDelete={handleDelete}
            togglePending={toggleMutation.isPending}
            deletePending={deleteMutation.isPending}
          />
        ))}
    </div>
  );
}

export default function UsersPage() {
  const { user: authUser, token } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'profile' | 'subaccounts' | 'tokens'>('profile');

  const isAccount = authUser?.user_type === 'account';

  const profileQuery = useQuery({
    queryKey: ['iam-profile', token],
    queryFn: async () => {
      if (!token) {
        throw new Error('no token');
      }
      return iamApi.getAccountProfile(token);
    },
    enabled: !!token,
  });

  const currentUser = profileQuery.data != null ? accountProfileToUser(profileQuery.data) : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">用户管理</h2>
        <p className="text-sm text-muted-foreground mt-1">管理您的账户信息和子账号</p>
      </div>

      <div className="flex gap-2 border-b">
        <button
          type="button"
          onClick={() => setActiveTab('profile')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'profile'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          我的信息
        </button>
        {isAccount && (
          <button
            type="button"
            onClick={() => setActiveTab('subaccounts')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'subaccounts'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            子账号管理
          </button>
        )}
        <button
          type="button"
          onClick={() => setActiveTab('tokens')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'tokens'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          访问令牌
        </button>
      </div>

      {activeTab === 'profile' && (
        <UserInfoCard user={currentUser} isLoading={profileQuery.isLoading} />
      )}

      {activeTab === 'subaccounts' && isAccount && <SubAccountList />}

      {activeTab === 'tokens' && <PersonalAccessTokenList />}
    </div>
  );
}
